import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ContentCard, EmptyState, LoadingState, PageHeader, Section } from '../components/public/ui';

const uploadUrl = (path) => {
  if (!path) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}/uploads/${path}`;
};

const Belts = () => {
  const [belts, setBelts] = useState([]);
  const [tariffs, setTariffs] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [beltsRes, tariffsRes] = await Promise.all([
          api.get('/public/belts'),
          api.get('/public/tariffs').catch(() => null),
        ]);
        setBelts(beltsRes.data.data);
        setTariffs(tariffsRes?.data?.data || null);
      } catch (error) {
        console.error('Error fetching belts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <LoadingState />;
  }

  const pdfUrl =
    tariffs?.content_type === 'pdf' && tariffs.file_path ? uploadUrl(tariffs.file_path) : null;
  const hasMarkdown =
    tariffs?.content_type === 'markdown' && Boolean(tariffs.markdown_body?.trim());

  return (
    <Section className="section-pad">
      <PageHeader
        title="Cinturones de color"
        lead="Conoce los requisitos para cada nivel del sistema de graduación."
      />

      {belts.length === 0 ? (
        <EmptyState title="Sin cinturones" description="Pronto publicaremos el sistema de grados." />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {belts.map((belt) => (
            <li key={belt.id}>
              <ContentCard className="flex h-full flex-col overflow-hidden !p-0">
                <div
                  className="h-2 w-full"
                  style={{ backgroundColor: belt.color_code || '#000' }}
                  aria-hidden="true"
                />
                <div className="flex flex-1 flex-col justify-between p-5 md:p-6">
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">
                        {belt.name}
                      </h2>
                      <span className="shrink-0 rounded-full border border-primary/30 px-2.5 py-1 text-xs font-semibold text-primary">
                        Nivel {belt.hierarchy_order}
                      </span>
                    </div>
                    <p className="mt-3 line-clamp-4 text-sm text-muted">{belt.description}</p>
                  </div>
                  <div className="mt-6 flex flex-col gap-2">
                    <button
                      type="button"
                      className="btn-primary w-full"
                      onClick={() => navigate(`/belts/${belt.id}`)}
                    >
                      Ver detalles
                    </button>
                    {belt.youtube_video_url && (
                      <a
                        href={belt.youtube_video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary w-full"
                      >
                        Video técnico
                      </a>
                    )}
                  </div>
                </div>
              </ContentCard>
            </li>
          ))}
        </ul>
      )}

      {(pdfUrl || hasMarkdown) && (
        <div className="mt-14 border-t border-line pt-10">
          <h2 className="text-center font-display text-2xl font-semibold uppercase tracking-wide">
            Tarifas
          </h2>

          {pdfUrl && (
            <div className="mt-6 text-center">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Descargar tarifas (PDF)
              </a>
            </div>
          )}

          {hasMarkdown && (
            <div className="tariffs-md mx-auto mt-6 max-w-2xl rounded-lg border border-line bg-surface p-6 text-left text-sm md:p-8">
              <ReactMarkdown>{tariffs.markdown_body}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

export default Belts;
